import i18n from '@/plugins/vue-i18n';
import { useToastStore } from '@/stores/utils/toast.store';

export type UserRoleToastScope = 'organisation' | 'project' | 'site';

const { t } = i18n.global;

/**
 * Shared toast wording for user/role changes on org, project, and site member lists —
 * matches the format established on the site roles dialog (PLE-928): "{role} has been
 * {action} for {user}". Wired into org and site (PLE-1069); project can adopt it once
 * confirmed working.
 */
export const showUserInvitedToast = (scope: UserRoleToastScope, email: string, role: string) => {
  useToastStore().showToast(
    'success',
    t('users.toasts.invite.title'),
    t('users.toasts.invite.content', { email, role, scope: t(`users.${scope}`) }),
  );
};

export const showUserRemovedToast = (
  scope: UserRoleToastScope,
  email: string,
  options?: { multiRole?: boolean },
) => {
  const key = options?.multiRole ? 'userRemovedMultiRole' : 'userRemoved';
  useToastStore().showToast(
    'success',
    t(`users.toasts.${key}.title`),
    t(`users.toasts.${key}.content`, { email, scope: t(`users.${scope}`) }),
  );
};

export const showRoleAddedToast = (email: string, role: string) => {
  useToastStore().showToast(
    'success',
    t('users.toasts.roleAdded.title'),
    t('users.toasts.roleAdded.content', { email, role }),
  );
};

export const showRoleRemovedToast = (email: string, role: string) => {
  useToastStore().showToast(
    'success',
    t('users.toasts.roleRemoved.title'),
    t('users.toasts.roleRemoved.content', { email, role }),
  );
};

export const showResendInviteToast = (email: string) => {
  useToastStore().showToast(
    'success',
    t('users.toasts.resendInvite.title'),
    t('users.toasts.resendInvite.content', { email }),
  );
};

/**
 * Client ↔ Client-with-Approval swap toast — direction-aware (grant vs revoke
 * approvals), same copy regardless of scope. Currently only site wires this in;
 * project has its own generic toast (see `updateProjectClientRole` in roles.store.ts)
 * and can move onto this once confirmed.
 */
export const showClientRoleSwitchToast = (email: string, toApproval: boolean) => {
  useToastStore().showToast(
    'success',
    t(
      toApproval
        ? 'users.toasts.clientRoleSwitch.toApproval.title'
        : 'users.toasts.clientRoleSwitch.toBase.title',
    ),
    t(
      toApproval
        ? 'users.toasts.clientRoleSwitch.toApproval.content'
        : 'users.toasts.clientRoleSwitch.toBase.content',
      { email },
    ),
  );
};
